/**
 * Geography for the 3D world, with no three.js dependency (unit-tested in Node).
 *
 * Country shapes: Natural Earth 1:110m via world-atlas (assets/geo/
 * countries-110m.json, TopoJSON). decodeTopology() turns it into plain
 * polygons in degrees. The globe paints them onto a texture
 * (equirectangular), and picks by looking up which polygon holds a point.
 *
 * The game's markets (every country in the world, backend/world_countries.py)
 * use their own names; ATLAS_NAME maps the ones that differ, and
 * MARKET_ANCHOR gives every market a point for markers, towers, arcs and the
 * camera. Small states (Singapore, Hong Kong, Malta, the island nations) have
 * no polygon at 1:110m, so they are drawn as city markers.
 */

const ATLAS_NAME = {
  "United States": "United States of America",
  UAE: "United Arab Emirates",
  // GENERATED atlas start (scripts/gen_world_countries.py)
  "Bosnia and Herzegovina": "Bosnia and Herz.",
  "Central African Republic": "Central African Rep.",
  "Czech Republic": "Czechia",
  "DR Congo": "Dem. Rep. Congo",
  "Dominican Republic": "Dominican Rep.",
  "Equatorial Guinea": "Eq. Guinea",
  "Eswatini": "eSwatini",
  "Ivory Coast": "Côte d'Ivoire",
  "North Macedonia": "Macedonia",
  "Solomon Islands": "Solomon Is.",
  "South Sudan": "S. Sudan",
  // GENERATED atlas end
};

/** [lat, lon] in degrees: the visual centre of each market on the globe. */
const MARKET_ANCHOR = {
  "United States": [39.5, -98.4],
  "United Kingdom": [53.0, -1.8],
  India: [22.5, 79.0],
  Germany: [51.1, 10.4],
  France: [46.6, 2.4],
  Singapore: [1.35, 103.82],
  UAE: [24.4, 54.4],
  Ireland: [53.3, -8.0],
  China: [35.0, 103.0],
  Japan: [36.5, 138.5],
  Canada: [56.0, -106.0],
  Australia: [-25.3, 134.0],
  Brazil: [-10.5, -52.9],
  Mexico: [23.6, -102.5],
  "South Korea": [36.4, 127.9],
  Netherlands: [52.2, 5.5],
  Switzerland: [46.8, 8.2],
  Sweden: [62.0, 15.0],
  Israel: [31.0, 34.85], // south of the West Bank polygon
  "South Africa": [-29.0, 24.7],
  Indonesia: [-1.0, 113.9], // Kalimantan (the 1:110m islands are coarse)
  Vietnam: [16.0, 107.8],
  "Saudi Arabia": [24.0, 45.0],
  Poland: [52.1, 19.4],
  Spain: [40.2, -3.6],
  Italy: [42.8, 12.6],
  Nigeria: [9.1, 8.7],
  Kenya: [0.2, 37.9],
  "New Zealand": [-41.5, 172.5],
  "Hong Kong": [22.32, 114.17],
  // every other country (backend/world_countries.py)
  // GENERATED anchors start (scripts/gen_world_countries.py)
  "Afghanistan": [33.9, 67.7],
  "Albania": [41.1, 20.1],
  "Algeria": [28.0, 2.6],
  "Andorra": [42.55, 1.6],
  "Angola": [-11.2, 17.9],
  "Antigua and Barbuda": [17.1, -61.8],
  "Argentina": [-35.0, -65.0],
  "Armenia": [40.2, 45.0],
  "Austria": [47.5, 14.6],
  "Azerbaijan": [40.3, 47.7],
  "Bahamas": [24.6, -77.9],
  "Bahrain": [26.07, 50.55],
  "Bangladesh": [23.7, 90.3],
  "Barbados": [13.19, -59.54],
  "Belarus": [53.7, 28.0],
  "Belgium": [50.6, 4.6],
  "Belize": [17.2, -88.7],
  "Benin": [9.5, 2.3],
  "Bhutan": [27.4, 90.4],
  "Bolivia": [-16.7, -64.7],
  "Bosnia and Herzegovina": [44.2, 17.8],
  "Botswana": [-22.3, 24.7],
  "Brunei": [4.5, 114.7],
  "Bulgaria": [42.7, 25.3],
  "Burkina Faso": [12.2, -1.6],
  "Burundi": [-3.4, 29.9],
  "Cambodia": [12.6, 104.9],
  "Cameroon": [5.7, 12.4],
  "Cape Verde": [15.1, -23.6],
  "Central African Republic": [6.6, 20.9],
  "Chad": [15.4, 18.7],
  "Chile": [-30.0, -71.0],
  "Colombia": [4.0, -73.0],
  "Comoros": [-11.7, 43.3],
  "Congo": [-0.7, 15.2],
  "Costa Rica": [9.9, -84.1],
  "Croatia": [45.3, 16.2],
  "Cuba": [21.9, -79.0],
  "Cyprus": [34.95, 33.0],
  "Czech Republic": [49.8, 15.5],
  "DR Congo": [-2.9, 23.6],
  "Denmark": [56.0, 9.3],
  "Djibouti": [11.8, 42.6],
  "Dominica": [15.41, -61.35],
  "Dominican Republic": [18.8, -70.4],
  "Ecuador": [-1.5, -78.4],
  "Egypt": [26.8, 30.8],
  "El Salvador": [13.75, -88.9],
  "Equatorial Guinea": [1.6, 10.5],
  "Eritrea": [15.2, 39.0],
  "Estonia": [58.7, 25.5],
  "Eswatini": [-26.5, 31.5],
  "Ethiopia": [8.6, 39.6],
  "Fiji": [-17.8, 178.0],
  "Finland": [63.0, 26.0],
  "Gabon": [-0.8, 11.6],
  "Gambia": [13.45, -15.4],
  "Georgia": [42.2, 43.5],
  "Ghana": [7.9, -1.0],
  "Greece": [39.3, 22.0],
  "Grenada": [12.12, -61.68],
  "Guatemala": [15.5, -90.3],
  "Guinea": [10.4, -10.9],
  "Guinea-Bissau": [11.9, -15.0],
  "Guyana": [4.8, -58.9],
  "Haiti": [19.0, -72.6],
  "Honduras": [14.8, -86.6],
  "Hungary": [47.2, 19.4],
  "Iceland": [64.9, -18.6],
  "Iran": [32.4, 53.7],
  "Iraq": [33.2, 43.7],
  "Ivory Coast": [7.6, -5.5],
  "Jamaica": [18.1, -77.3],
  "Jordan": [31.2, 36.5],
  "Kazakhstan": [48.0, 67.0],
  "Kiribati": [1.45, 173.0],
  "Kosovo": [42.6, 20.9],
  "Kuwait": [29.3, 47.6],
  "Kyrgyzstan": [41.2, 74.8],
  "Laos": [19.9, 102.5],
  "Latvia": [56.9, 24.6],
  "Lebanon": [33.9, 35.9],
  "Lesotho": [-29.6, 28.2],
  "Liberia": [6.4, -9.4],
  "Libya": [27.0, 17.2],
  "Liechtenstein": [47.16, 9.55],
  "Lithuania": [55.3, 23.9],
  "Luxembourg": [49.8, 6.1],
  "Madagascar": [-19.4, 46.7],
  "Malawi": [-13.3, 34.0],
  "Malaysia": [4.2, 101.9],
  "Maldives": [3.2, 73.2],
  "Mali": [17.6, -3.5],
  "Malta": [35.9, 14.4],
  "Marshall Islands": [7.1, 171.2],
  "Mauritania": [20.3, -10.9],
  "Mauritius": [-20.3, 57.6],
  "Micronesia": [6.9, 158.2],
  "Moldova": [47.2, 28.5],
  "Monaco": [43.74, 7.42],
  "Mongolia": [46.9, 103.8],
  "Montenegro": [42.7, 19.3],
  "Morocco": [31.8, -7.1],
  "Mozambique": [-18.7, 35.5],
  "Myanmar": [21.9, 95.9],
  "Namibia": [-22.6, 17.1],
  "Nauru": [-0.52, 166.93],
  "Nepal": [28.4, 84.1],
  "Nicaragua": [12.9, -85.0],
  "Niger": [17.6, 8.1],
  "North Macedonia": [41.6, 21.7],
  "Norway": [61.5, 9.0],
  "Oman": [21.0, 57.0],
  "Pakistan": [30.4, 69.3],
  "Palau": [7.5, 134.6],
  "Palestine": [31.9, 35.2],
  "Panama": [8.5, -80.1],
  "Papua New Guinea": [-6.3, 143.9],
  "Paraguay": [-23.4, -58.4],
  "Peru": [-9.5, -75.0],
  "Philippines": [12.9, 121.8],
  "Portugal": [39.6, -8.0],
  "Qatar": [25.3, 51.2],
  "Romania": [45.9, 24.9],
  "Russia": [60.0, 90.0],
  "Rwanda": [-2.0, 29.9],
  "Saint Kitts and Nevis": [17.3, -62.75],
  "Saint Lucia": [13.9, -60.97],
  "Saint Vincent and the Grenadines": [13.25, -61.2],
  "Samoa": [-13.75, -172.1],
  "San Marino": [43.94, 12.46],
  "Sao Tome and Principe": [0.2, 6.6],
  "Senegal": [14.5, -14.5],
  "Serbia": [44.0, 20.9],
  "Seychelles": [-4.68, 55.49],
  "Sierra Leone": [8.5, -11.8],
  "Slovakia": [48.7, 19.7],
  "Slovenia": [46.1, 14.8],
  "Solomon Islands": [-9.6, 160.2],
  "Somalia": [5.2, 46.2],
  "South Sudan": [7.3, 30.3],
  "Sri Lanka": [7.9, 80.8],
  "Sudan": [15.6, 30.2],
  "Suriname": [4.1, -55.9],
  "Syria": [35.0, 38.5],
  "Taiwan": [23.7, 121.0],
  "Tajikistan": [38.9, 71.3],
  "Tanzania": [-6.4, 34.9],
  "Thailand": [15.9, 101.0],
  "Timor-Leste": [-8.8, 125.7],
  "Togo": [8.6, 0.9],
  "Tonga": [-21.2, -175.2],
  "Trinidad and Tobago": [10.45, -61.25],
  "Tunisia": [34.0, 9.5],
  "Turkey": [39.0, 35.0],
  "Turkmenistan": [39.0, 59.6],
  "Tuvalu": [-8.5, 179.2],
  "Uganda": [1.4, 32.3],
  "Ukraine": [49.0, 31.5],
  "Uruguay": [-32.8, -56.0],
  "Uzbekistan": [41.4, 64.6],
  "Vanuatu": [-15.4, 166.9],
  "Venezuela": [7.1, -66.2],
  "Yemen": [15.6, 48.0],
  "Zambia": [-13.1, 27.8],
  "Zimbabwe": [-19.0, 29.9],
  // GENERATED anchors end
};

const atlasName = (market) => ATLAS_NAME[market] || market;
const marketForAtlas = (name) => Object.keys(ATLAS_NAME).find((k) => ATLAS_NAME[k] === name) || name;

/**
 * TopoJSON -> [{ name, id, polygons: [[ring, ...holes], ...] }], rings as
 * [[lon, lat], ...]. Handles quantized (delta-encoded) arcs and reversed
 * arc references (~index).
 */
function decodeTopology(topo, objectName = "countries") {
  const tf = topo.transform;
  const arcs = topo.arcs.map((arc) => {
    let x = 0, y = 0;
    return arc.map((p) => {
      if (tf) {
        x += p[0]; y += p[1];
        return [x * tf.scale[0] + tf.translate[0], y * tf.scale[1] + tf.translate[1]];
      }
      return [p[0], p[1]];
    });
  });
  const ring = (indices) => {
    const out = [];
    for (const i of indices) {
      const a = i < 0 ? arcs[~i].slice().reverse() : arcs[i];
      // consecutive arcs share their joining point
      out.push(...(out.length ? a.slice(1) : a));
    }
    return out;
  };
  return topo.objects[objectName].geometries.map((g) => {
    const polys = g.type === "Polygon" ? [g.arcs] : g.type === "MultiPolygon" ? g.arcs : [];
    return { name: g.properties && g.properties.name, id: g.id, polygons: polys.map((p) => p.map(ring)) };
  });
}

/** Ray-casting point-in-ring test, [lon, lat] coordinates. */
function inRing(lon, lat, ring) {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i], [xj, yj] = ring[j];
    if ((yi > lat) !== (yj > lat) && lon < ((xj - xi) * (lat - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

/** The country containing [lon, lat] (outer ring in, not in a hole), or null. */
function countryAt(countries, lon, lat) {
  for (const c of countries) {
    for (const [outer, ...holes] of c.polygons) {
      if (inRing(lon, lat, outer) && !holes.some((h) => inRing(lon, lat, h))) return c;
    }
  }
  return null;
}

/** Unit-sphere position for [lat, lon]: +Y up, lon 0 facing +Z (three.js convention). */
function latLonToXYZ(lat, lon, r = 1) {
  const phi = (lat * Math.PI) / 180, lam = (lon * Math.PI) / 180;
  return [r * Math.cos(phi) * Math.sin(lam), r * Math.sin(phi), r * Math.cos(phi) * Math.cos(lam)];
}

/** Inverse of latLonToXYZ, for any length vector. */
function xyzToLatLon(x, y, z) {
  const r = Math.hypot(x, y, z) || 1;
  return [(Math.asin(y / r) * 180) / Math.PI, (Math.atan2(x, z) * 180) / Math.PI];
}

/**
 * Equirectangular texture pixel for [lat, lon]: lon -180 at x = 0, lon 180
 * at x = width, so the seam falls in the Pacific. three.js SphereGeometry puts
 * u = 0.5 at +X, so the globe mesh is turned -90 degrees about Y (GLOBE_YAW)
 * to bring lon 0 to +Z and match latLonToXYZ.
 */
function latLonToPixel(lat, lon, width, height) {
  return [((lon + 180) / 360) * width, ((90 - lat) / 180) * height];
}

/** [lat, lon] for an equirectangular pixel (inverse of latLonToPixel). */
function pixelToLatLon(x, y, width, height) {
  return [90 - (y / height) * 180, (x / width) * 360 - 180];
}

const GLOBE_YAW = -Math.PI / 2;

/** A ring with continuous longitudes (no 360-degree jumps across the antimeridian). */
function unwrapRing(ring) {
  const out = [];
  let offset = 0, prev = null;
  for (const [lon, lat] of ring) {
    if (prev !== null) {
      if (lon - prev > 180) offset -= 360;
      else if (prev - lon > 180) offset += 360;
    }
    out.push([lon + offset, lat]);
    prev = lon;
  }
  return out;
}

/** Great-circle midpoint lifted off the surface: control point for an arc. */
function arcControl(a, b, lift = 0.35) {
  const pa = latLonToXYZ(a[0], a[1]), pb = latLonToXYZ(b[0], b[1]);
  const m = [pa[0] + pb[0], pa[1] + pb[1], pa[2] + pb[2]];
  const len = Math.hypot(...m) || 1;
  const dist = Math.acos(Math.max(-1, Math.min(1, pa[0] * pb[0] + pa[1] * pb[1] + pa[2] * pb[2])));
  const r = 1 + lift * dist;
  return [(m[0] / len) * r, (m[1] / len) * r, (m[2] / len) * r];
}

export { ATLAS_NAME, GLOBE_YAW, MARKET_ANCHOR, arcControl, atlasName, countryAt, decodeTopology, inRing, latLonToPixel, latLonToXYZ, marketForAtlas, pixelToLatLon, unwrapRing, xyzToLatLon };
